import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, money, fmtDate } from "@/lib/api";
import { BRAND, stateName, coverageLabel, levelLabel } from "@/lib/constants";

const Cell = ({ k, v }) => (
  <div className="border border-slate-300 p-3">
    <p className="text-[10px] uppercase tracking-wider text-slate-500">{k}</p>
    <p className="mt-1 font-semibold">{v}</p>
  </div>
);

export default function Certificate() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/policies/${id}`).then((r) => setP(r.data)).catch(() => setErr("Certificate not found."));
  }, [id]);
  if (err) return <p className="p-10">{err}</p>;
  if (!p) return null;
  if (p.filing_status !== "certificate_active") return <p className="p-10" data-testid="certificate-not-ready">Your certificate isn't ready yet. It becomes available once the state accepts your filing.</p>;
  const issued = p.filing_history.filter((h) => h.status === "certificate_active").pop();
  return (
    <div className="min-h-screen bg-slate-100 py-10 print:bg-white print:py-0">
      <div className="no-print mx-auto mb-4 flex max-w-3xl justify-end px-4">
        <Button onClick={() => window.print()} className="bg-blue-700 text-white hover:bg-blue-800 hover:text-white" data-testid="certificate-print-btn">
          <Printer className="mr-2 h-4 w-4" /> Print / Save as PDF
        </Button>
      </div>
      <div className="mx-auto max-w-3xl border-2 border-slate-900 bg-white p-10" data-testid="sr22-certificate">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-6">
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Proof of financial responsibility</p>
            <h1 className="mt-2 text-3xl font-extrabold tracking-tight">SR22 Certificate of Insurance</h1>
            <p className="mt-1 text-sm text-slate-600">State of {stateName(p.state)}</p>
          </div>
          <div className="text-right">
            <ShieldCheck className="ml-auto h-10 w-10 text-emerald-600" />
            <p className="mt-1 font-bold">{BRAND.name}</p>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2">
          <Cell k="Insured" v={p.insured_name} />
          <Cell k="Policy number" v={<span className="font-mono">{p.policy_number}</span>} />
          <Cell k="Policy type" v={coverageLabel(p.coverage_type)} />
          <Cell k="Coverage" v={levelLabel(p.coverage_level)} />
          <Cell k="Effective date" v={fmtDate(p.effective_date)} />
          <Cell k="Expiration date" v={fmtDate(p.expiry_date)} />
          <Cell k="Vehicle" v={p.vehicle || "Non-owner — any non-owned vehicle"} />
          <Cell k="Monthly premium" v={money(p.monthly_premium)} />
        </div>
        <p className="mt-6 text-sm leading-relaxed text-slate-700">
          This certifies that the insured named above holds an automobile liability policy meeting the financial responsibility requirements of the State of {stateName(p.state)}, and that an SR22 filing has been submitted to and accepted by the state motor vehicle agency{issued ? ` on ${fmtDate(issued.at)}` : ""}. The insurer will notify the state if this policy is cancelled or lapses.
        </p>
        <div className="mt-10 flex items-end justify-between text-xs text-slate-500">
          <p>Issued by {BRAND.name}</p>
          <p className="font-mono">Generated {new Date().toLocaleDateString()}</p>
        </div>
      </div>
    </div>
  );
}
